import React, { useEffect, useState } from 'react';
import { Feedback, errorMessage } from '../api/client';
import { FeedbackData, FeedbackType, World } from '../types';
import { Note } from './ui/primitives';

interface Props {
  open: boolean;
  onClose: () => void;
  worlds: World[];
  defaultWorldId?: string | null;
}

const TYPES: { value: FeedbackType; label: string }[] = [
  { value: 'bug', label: 'Bug report' },
  { value: 'feature', label: 'Feature request' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'other', label: 'Other' },
];

/** Feedback → POST /api/feedback (creates a GitHub issue server-side). */
const FeedbackDialog: React.FC<Props> = ({ open, onClose, worlds, defaultWorldId }) => {
  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [expected, setExpected] = useState('');
  const [actual, setActual] = useState('');
  const [worldId, setWorldId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setType('bug');
    setTitle('');
    setDescription('');
    setSteps('');
    setExpected('');
    setActual('');
    setWorldId(defaultWorldId || '');
    setSubmitting(false);
    setDone(false);
    setError(null);
  }, [open, defaultWorldId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !submitting && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;
  const isBug = type === 'bug';
  const valid = title.trim().length > 0 && description.trim().length > 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) {
      setError('Please fill in both a title and a description.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const data: FeedbackData = { type, title: title.trim(), description: description.trim() };
    if (isBug) {
      if (steps.trim()) data.stepsToReproduce = steps.trim();
      if (expected.trim()) data.expectedBehavior = expected.trim();
      if (actual.trim()) data.actualBehavior = actual.trim();
      if (worldId) data.worldId = worldId;
    }
    try {
      await Feedback.submit(data);
      setDone(true);
      window.setTimeout(onClose, 1800);
    } catch (err) {
      setError(errorMessage(err, 'Failed to submit feedback. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-stage" onClick={() => !submitting && onClose()}>
      <form className="modal modal--wide" onClick={(e) => e.stopPropagation()} onSubmit={submit} role="dialog" aria-modal="true" aria-label="Send feedback">
        <h3>Send feedback</h3>
        <p>Bugs, ideas and improvements go straight to the team as a GitHub issue.</p>
        {done && <Note tone="ok">Thank you! Your feedback has been filed and the team will take a look.</Note>}
        {error && <Note tone="danger">{error}</Note>}
        <div className="form">
          <div style={{ display: 'grid', gridTemplateColumns: isBug ? '1fr 1fr' : '1fr', gap: 14 }}>
            <div className="field">
              <label htmlFor="fb-type">Type</label>
              <select id="fb-type" className="input" value={type} onChange={(e) => setType(e.target.value as FeedbackType)} disabled={submitting}>
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            {isBug && (
              <div className="field">
                <label htmlFor="fb-world">
                  Related world <i>(optional)</i>
                </label>
                <select id="fb-world" className="input" value={worldId} onChange={(e) => setWorldId(e.target.value)} disabled={submitting}>
                  <option value="">None / general issue</option>
                  {worlds.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.status})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="field">
            <label htmlFor="fb-title">Title</label>
            <input
              id="fb-title"
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
              placeholder={isBug ? "Brief description of the bug (e.g. 'Backups tab not loading')" : 'Brief summary of your feedback'}
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor="fb-desc">Description</label>
            <textarea
              id="fb-desc"
              className="input"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              placeholder={isBug ? 'Describe what happened and any error messages you saw' : 'Tell us more'}
            />
          </div>
          {isBug && (
            <>
              <div className="field">
                <label htmlFor="fb-steps">
                  Steps to reproduce <i>(optional)</i>
                </label>
                <textarea id="fb-steps" className="input" rows={3} value={steps} onChange={(e) => setSteps(e.target.value)} disabled={submitting} placeholder={'1. Go to…\n2. Click on…\n3. See error'} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="field">
                  <label htmlFor="fb-exp">
                    Expected <i>(optional)</i>
                  </label>
                  <textarea id="fb-exp" className="input" rows={2} value={expected} onChange={(e) => setExpected(e.target.value)} disabled={submitting} placeholder="What did you expect?" />
                </div>
                <div className="field">
                  <label htmlFor="fb-act">
                    Actual <i>(optional)</i>
                  </label>
                  <textarea id="fb-act" className="input" rows={2} value={actual} onChange={(e) => setActual(e.target.value)} disabled={submitting} placeholder="What actually happened?" />
                </div>
              </div>
            </>
          )}
          <small style={{ color: 'var(--ink3)', fontSize: 11.5 }}>Please don't include passwords or personal data.</small>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={submitting || !valid || done}>
            {submitting && <span className="spin" style={{ borderTopColor: '#fff' }} />}
            {submitting ? 'Sending…' : 'Send feedback'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FeedbackDialog;
